import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { SpecraButton } from '../ensigns/button';
import { SpecraCheckbox } from '../ensigns/checkbox';
import { SpecraProgress } from '../ensigns/progress';
import { SpecraStatusDot } from '../ensigns/status-dot';
import { SpecraText } from '../ensigns/typography';
function readTicks(key) {
    if (!key) return {};
    try {
        const raw = window.localStorage.getItem(key);
        const parsed = raw ? JSON.parse(raw) : {};
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        const out = {};
        for (const [k, v] of Object.entries(parsed))if (typeof v === 'boolean') out[k] = v;
        return out;
    } catch  {
        return {};
    }
}
function writeTicks(key, ticks) {
    if (!key) return;
    try {
        if (Object.keys(ticks).length === 0) window.localStorage.removeItem(key);
        else window.localStorage.setItem(key, JSON.stringify(ticks));
    } catch  {
    // Storage blocked or full: the ticks still hold for this visit.
    }
}
/** A tickable list with a progress header. */ export function SpecraChecklist({ title, items, storageKey, source, className }) {
    // Only the viewer's changes are stored; an item never touched shows the reply's state.
    const [ticks, setTicks] = React.useState(()=>readTicks(storageKey));
    React.useEffect(()=>{
        setTicks(readTicks(storageKey));
    }, [
        storageKey
    ]);
    const isDone = (item)=>ticks[item.id] ?? Boolean(item.done);
    const doneCount = items.filter(isDone).length;
    const pct = items.length ? doneCount / items.length * 100 : 0;
    const changed = Object.keys(ticks).length > 0;
    const headingId = React.useId();
    const toggle = (item, next)=>{
        setTicks((prev)=>{
            const out = {
                ...prev
            };
            if (next === Boolean(item.done)) delete out[item.id];
            else out[item.id] = next;
            writeTicks(storageKey, out);
            return out;
        });
    };
    const reset = ()=>{
        setTicks({});
        writeTicks(storageKey, {});
    };
    return /*#__PURE__*/ _jsxs("section", {
        "aria-labelledby": title ? headingId : undefined,
        "aria-label": title ? undefined : 'Checklist',
        className: cn('rounded-md border border-border bg-card p-[var(--space-4)]', className),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-baseline justify-between gap-[var(--space-3)]",
                children: [
                    title ? /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "font-medium",
                        id: headingId,
                        size: "body-sm",
                        children: title
                    }) : /*#__PURE__*/ _jsx("span", {}),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex shrink-0 items-center gap-[var(--space-2)]",
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                "aria-live": "polite",
                                className: "tabular-nums",
                                size: "caption",
                                tone: "muted",
                                children: [
                                    doneCount,
                                    " of ",
                                    items.length,
                                    " done"
                                ]
                            }),
                            changed && /*#__PURE__*/ _jsx(SpecraButton, {
                                onClick: reset,
                                size: "inline",
                                type: "button",
                                variant: "link",
                                children: "Reset"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraProgress, {
                "aria-label": `${doneCount} of ${items.length} done`,
                className: "mt-[var(--space-2)]",
                size: "thin",
                tone: doneCount === items.length && items.length > 0 ? 'success' : 'brand',
                value: pct
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "mt-[var(--space-3)] divide-y divide-border/70",
                children: items.map((item)=>/*#__PURE__*/ _jsx(ChecklistRow, {
                        item: item,
                        done: isDone(item),
                        onToggle: toggle
                    }, item.id))
            }),
            source && /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                className: "mt-[var(--space-2)]",
                size: "caption",
                tone: "muted",
                children: [
                    "Source: ",
                    source
                ]
            })
        ]
    });
}
function ChecklistRow({ item, done, onToggle }) {
    const id = React.useId();
    const meta = [
        item.owner,
        item.due ? `Due ${item.due}` : ''
    ].filter(Boolean).join(' · ');
    return /*#__PURE__*/ _jsxs("li", {
        className: "flex items-start gap-[var(--space-2-5)] py-[var(--space-2)]",
        children: [
            /*#__PURE__*/ _jsx(SpecraCheckbox, {
                checked: done,
                className: "mt-[var(--space-0-5)]",
                id: id,
                onCheckedChange: (v)=>onToggle(item, v === true)
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "min-w-0 flex-1",
                children: [
                    /*#__PURE__*/ _jsxs("label", {
                        className: "flex cursor-pointer items-center gap-[var(--space-1-5)]",
                        htmlFor: id,
                        children: [
                            item.tone && item.tone !== 'neutral' && /*#__PURE__*/ _jsx(SpecraStatusDot, {
                                tone: item.tone
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                className: cn(done && 'text-muted-foreground line-through'),
                                size: "body-sm",
                                children: item.label
                            })
                        ]
                    }),
                    item.detail && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "caption",
                        tone: "muted",
                        children: item.detail
                    }),
                    meta && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "tabular-nums",
                        size: "micro",
                        tone: "muted",
                        children: meta
                    })
                ]
            })
        ]
    });
}
